import { getDefaultClient } from '../../../client/defaultClient';
import { assertWalletProviderMethodDefined } from '../utils/assertWalletProviderMethodDefined';
import { getWalletProviderFromWalletAccount } from '../utils/getWalletProviderFromWalletAccount';
import type { TransferAmountParams } from '../walletProvider/walletProvider.types';

/**
 * Transfers an amount of currency from the wallet account to a recipient.
 *
 * Supports native token transfers and, where supported by the chain,
 * fungible/token transfers via the optional `token` parameter.
 *
 * @param params.walletAccount - The wallet account to transfer from.
 * @param params.amount - The amount to transfer as a decimal string (e.g. "1.5").
 * @param params.recipient - The recipient address.
 * @param params.token - Optional token info for non-native transfers.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to an object containing the transaction hash.
 * @throws If the wallet provider does not support the transfer amount method.
 */
export const transferAmount = async (
  { walletAccount, amount, recipient, token }: TransferAmountParams,
  client = getDefaultClient()
): Promise<{ transactionHash: string }> => {
  const walletProvider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  assertWalletProviderMethodDefined(walletProvider, 'transferAmount');

  const { transactionHash } = await walletProvider.transferAmount({
    amount,
    recipient,
    token,
    walletAccount,
  });

  return { transactionHash };
};
